/**
 * signalData.js
 * Demo signal card data — sourced from verified news as of March 2026.
 *
 * PRODUCTION: Replace with API fetch from your backend:
 *   GET /api/signals → returns { china: [...], corridor: [...], flash: [...], india: [...], capital: [...] }
 *
 * Event shape:
 *   id        string  — unique, used for modal + brief-ref cross-linking
 *   title     string  — headline text
 *   summary   string  — full paragraph for modal detail view
 *   time      string  — display date (human-readable)
 *   tag       string  — 'HIGH' | 'MED' | 'NEW' | 'REG'
 *   source    string  — source attribution
 *   lat/lng   number  — optional, used to place map pin
 *   type      string  — map pin category: 'china'|'corridor'|'flash'|'india'|'capital'
 */

export const SIGNALS = {
  china: {
    label: 'China Watch',
    icon: '🔴',
    countLabel: '2 today',
    lead: {
      id: 'china1',
      label: 'Lead Story',
      title: 'CNS Fujian (003) carrier conducts first strike group exercise — PLAN now operates three carriers simultaneously',
      summary: 'China\'s third and most advanced carrier CNS Fujian (Type 003, CATOBAR) conducted its first documented strike group exercise in the South China Sea, operating alongside destroyers and a replenishment vessel. USNI News and AllSource Analysis satellite imagery confirm all three PLAN carriers were underway simultaneously for the first time — Liaoning in the Western Pacific, Shandong at Yulin, Fujian in the South China Sea. Analysts assess the milestone as PLAN declaring initial operational capability for multi-carrier power projection.',
      time: 'Nov 2025',
      tag: 'HIGH',
      source: 'USNI News · China Military Online',
      lat: 15.5, lng: 114.0, type: 'china',
    },
    events: [
      {
        id: 'china2',
        title: 'Shandong enters new Yulin Naval Base dry dock — South China Sea carrier sustainment now independent of Dalian',
        summary: 'PLA Southern Theater Command has completed a new deep-water dry dock facility at Yulin Naval Base on Hainan Island capable of servicing Type 001A Shandong-class carriers. The development eliminates the previous requirement to transit to Dalian for maintenance, giving PLAN full South China Sea carrier sustainment capability for the first time. AllSource Analysis satellite imagery from January 21 confirms Shandong berthed in the new facility.',
        time: 'Jan 21',
        tag: 'MED',
        source: 'AllSource Analysis · SCMP',
        lat: 18.2, lng: 109.5, type: 'china',
      },
    ],
  },

  corridor: {
    label: 'Defense Corridor',
    icon: '🔵',
    countLabel: '3 today',
    lead: {
      id: 'corridor1',
      label: 'Lead Story',
      title: 'Rolls-Royce confirms full IP transfer for combat engine co-development with India — first such commitment from a Tier 1 defense OEM',
      summary: 'Rolls-Royce plc CEO Tufan Erginbilgic, speaking at the Aero India 2025 follow-up industry roundtable, confirmed that the company\'s combat engine co-development proposal with India will include full intellectual property transfer rights — a structurally significant departure from standard defense OEM practice. The commitment aligns with India\'s Atmanirbhar Bharat requirements and would give DRDO and HAL full rights to manufacture and export derivatives. No contract has been signed. DSCA notification expected in Q2 2026.',
      time: 'Mar 12',
      tag: 'HIGH',
      source: 'Defense News · PIB',
      lat: 28.6, lng: 77.2, type: 'corridor',
    },
    events: [
      {
        id: 'corridor2',
        title: 'GE confirms 30 F404-IN20 engines annually from FY2027–28 — Tejas Mk1A production bottleneck cleared',
        summary: 'General Electric confirmed delivery schedule of 30 F404-IN20 engines per year to HAL beginning FY2027–28, clearing the primary production bottleneck for the Tejas Mk1A program. The previous delivery delay of 99 contracted engines had held up IAF induction. With the schedule confirmed, HAL can now commit to the 18-aircraft-per-year production rate required under the ₹48,000 crore ($558M) contract for 83 Tejas Mk1A fighters. First deliveries of the 30-aircraft tranche remain on track for 2025.',
        time: 'Mar 10',
        tag: 'MED',
        source: 'PIB · Defense News',
        lat: 12.9, lng: 77.5, type: 'corridor',
      },
      {
        id: 'corridor3',
        title: 'Ghatak UCAV clears Development Board — stealth combat drone program enters full-scale development phase',
        summary: 'India\'s Ghatak (meaning "lethal" in Hindi) autonomous stealth combat air vehicle cleared its Development Board review, authorizing full-scale development funding. The aircraft — a low-observable UCAV designed for penetrating strike missions — is a DRDO ADE program and represents India\'s first indigenously developed combat-capable drone. The clearance follows successful wind tunnel tests and preliminary design reviews. First flight is targeted for FY2027. The program has strategic significance for India\'s ability to field a credible stealth strike capability without dependency on foreign suppliers.',
        time: 'Mar 11',
        tag: 'NEW',
        source: 'IDRW · PIB',
        lat: 17.4, lng: 78.5, type: 'corridor',
      },
    ],
  },

  flash: {
    label: 'Flashpoints & Maritime',
    icon: '🟡',
    countLabel: '3 today',
    lead: {
      id: 'flash1',
      label: 'Lead Story',
      title: 'Hormuz Day 12: Strait closed, one Indian sailor killed on tanker Safesea Vishnu — Iraq shuts all oil export terminals',
      summary: 'The Strait of Hormuz entered its 12th consecutive day of full closure. An Iranian suicide boat attack on the Indian-flagged tanker Safesea Vishnu near Khor Al Zubair killed one Indian national; 27 crew were evacuated to Basra port. Iraq simultaneously shut all oil export terminals following an Iranian marine attack that killed at least one person at Basra. Brent crude crossed $100/barrel. Iran has stated that Indian-flagged vessels will receive safe passage following three calls between EAM Jaishankar and Iran FM Araghchi, though an Iranian official denied this characterization to Reuters. India co-sponsored a UNSC resolution condemning the attacks.',
      time: 'Mar 12',
      tag: 'HIGH',
      source: 'USNI News · IFC-IOR · Reuters',
      lat: 26.5, lng: 56.3, type: 'flash',
    },
    events: [
      {
        id: 'flash2',
        title: 'PLAN PLA-N task group — amphibious ship Kunlunshan (071 LPD) enters Arabian Sea for third deployment this quarter',
        summary: 'CNS Kunlunshan (Type 071 amphibious transport dock) entered the Arabian Sea as part of a three-ship PLAN task group, marking the third PLAN deployment to the region in the current quarter. The group includes a destroyer and a Type 903 replenishment vessel. USNI News tracking confirms departure from Djibouti PLAN Support Base on March 9. Analysts note the operational tempo is elevated relative to PLAN\'s historical Arabian Sea presence. The task group\'s declared mission is counter-piracy; actual tasking in the context of Hormuz closure is assessed as multi-mission.',
        time: 'Mar 11',
        tag: 'MED',
        source: 'USNI News · Ambrey',
        lat: 14.2, lng: 56.8, type: 'flash',
      },
      {
        id: 'flash3',
        title: 'GPS jamming reported across 180,000 sq km — affecting civil aviation and shipping in the Eastern Mediterranean and Red Sea approach',
        summary: 'GPSJam.org monitoring recorded a sustained GPS/GNSS interference zone covering approximately 180,000 sq km, stretching from the Eastern Mediterranean across the Red Sea approach and into the Gulf of Aden. The interference pattern is consistent with military jamming operations. Multiple commercial aircraft reported GPS anomalies requiring IRS backup navigation. At least 14 vessels in the affected zone reported AIS positioning inconsistencies consistent with spoofing. The FAA has issued a NOTAM for affected regions. Source attribution has not been confirmed publicly.',
        time: 'Mar 10',
        tag: 'MED',
        source: 'GPSJam.org · FAA NOTAM',
        lat: 22.5, lng: 43.0, type: 'flash',
      },
    ],
  },

  india: {
    label: 'India Strategic Calculus',
    icon: '🟢',
    countLabel: '2 today',
    lead: {
      id: 'india1',
      label: 'Lead Story',
      title: 'EAM Jaishankar holds third call with Iran FM Araghchi — India co-sponsors UNSC resolution, evacuates 9,000 nationals',
      summary: 'External Affairs Minister S. Jaishankar held his third call with Iranian Foreign Minister Abbas Araghchi since the Hormuz closure began, securing what Indian sources characterize as an assurance of safe passage for Indian-flagged vessels. An Iranian official speaking to Reuters declined to confirm the characterization, calling it "ongoing discussions." India co-sponsored a UNSC resolution with 130+ nations condemning Iran\'s attacks on Gulf state infrastructure — a notable shift from India\'s typical abstention posture on resolutions involving major oil suppliers. The MEA confirmed evacuation of 9,000 Indian nationals from Iran is underway, with 28 Indian-flagged vessels currently stranded in the region.',
      time: 'Mar 12',
      tag: 'HIGH',
      source: 'MEA India · Reuters',
      lat: 28.6, lng: 77.2, type: 'india',
    },
    events: [
      {
        id: 'india2',
        title: 'India DAC clears ₹11,500 crore ($134M) procurement for 48 Rafale Marine fighters for Navy — Cabinet approval pending',
        summary: 'India\'s Defence Acquisition Council (DAC) granted Acceptance of Necessity (AoN) for procurement of 26 Rafale Marine fighters for the Indian Navy\'s INS Vikrant carrier operations, with an option for an additional 22 aircraft. The total programme value is estimated at ₹11,500 crore ($134M at current rates). The procurement follows the 2022 IAF Rafale experience and is under the government-to-government route with France. Cabinet Committee on Security (CCS) approval is pending. First deliveries are expected within 5–6 years of contract signing.',
        time: 'Mar 9',
        tag: 'NEW',
        source: 'PIB · IDRW',
        lat: 15.3, lng: 73.8, type: 'india',
      },
    ],
  },

  capital: {
    label: 'Capital & Industry',
    icon: '🟠',
    countLabel: '2 today',
    lead: {
      id: 'capital1',
      label: 'Lead Story',
      title: 'DPIIT clears 100% FDI in defense manufacturing under automatic route for dual-use technology JVs',
      summary: 'India\'s Department for Promotion of Industry and Internal Trade (DPIIT) issued a policy clarification extending 100% FDI under the automatic route to dual-use technology joint ventures in defense manufacturing — covering components and sub-systems used in both military and civilian applications. The clarification resolves a long-standing ambiguity that had slowed investment in the sector. U.S. and Israeli industry sources told Business Standard the change could unlock several pending JV proposals. The FDI policy update follows the DAC\'s broader defense industrialization push and is expected to accelerate investment in areas including surveillance electronics, propulsion components, and advanced materials.',
      time: 'Mar 11',
      tag: 'NEW',
      source: 'DPIIT · Business Standard',
      lat: 28.6, lng: 77.2, type: 'capital',
    },
    events: [
      {
        id: 'capital2',
        title: 'BIS adds 42 Chinese entities to Entity List — includes three PLA-linked semiconductor firms with India operations',
        summary: 'The U.S. Bureau of Industry and Security (BIS) added 42 Chinese entities to the Entity List, including three PLA-linked semiconductor firms that have disclosed operations or partnerships in India. The additions require U.S. exporters to obtain a license before shipping to listed entities. India-based subsidiaries or JV partners of listed firms must now assess their supply chain exposure to U.S.-origin technology. The BIS action is the third China-focused Entity List update in FY2026. Legal counsel is recommended for Indian firms with upstream U.S. technology exposure and downstream Chinese entity partnerships.',
        time: 'Mar 12',
        tag: 'HIGH',
        source: 'BIS · Economic Times',
        lat: 28.6, lng: 77.2, type: 'capital',
      },
    ],
  },
};

/**
 * All events flattened — used by the map component to render pins.
 * Only events with lat/lng are included.
 */
export const ALL_MAP_EVENTS = Object.entries(SIGNALS).flatMap(([cardKey, card]) => {
  const all = [card.lead, ...(card.events || [])];
  return all.filter(e => e.lat && e.lng).map(e => ({ ...e, cardKey }));
});
